import React, { useState } from 'react';
import { Brain, Eye, Ear, AlertTriangle, Smile, Hand, Heart, Wind, Users, DollarSign, Shield, Gamepad2, BookOpen, Activity, Stethoscope, Cpu, Zap, ChevronRight, ChevronDown, ChevronUp, X, Layers, Target, Rocket, Cog } from 'lucide-react';

const ZeuronOrgChart = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});

  // Enhanced security measures
  React.useEffect(() => {
    // Disable right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable text selection
    const handleSelectStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable F12, Ctrl+Shift+I, Ctrl+U, Ctrl+S, Ctrl+A, Ctrl+C, Ctrl+P
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.key === 'u') ||
        (e.ctrlKey && e.key === 's') ||
        (e.ctrlKey && e.key === 'a') ||
        (e.ctrlKey && e.key === 'c') ||
        (e.ctrlKey && e.key === 'p')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Global controls for expand/collapse all
  const allSections = ['nervous-system', 'organ-systems', 'body-functions', 'neural-substrate', 'reflex-arc'];
  
  const expandAll = () => {
    setCollapsedSections({});
  };

  const collapseAll = () => {
    const collapsed = {};
    allSections.forEach(section => {
      collapsed[section] = true;
    });
    setCollapsedSections(collapsed);
  };

  // Count expanded sections
  const expandedCount = allSections.filter(section => 
    collapsedSections[section] !== true
  ).length;

  // Prevent search engine indexing
  React.useEffect(() => {
    // Add comprehensive meta tags to prevent search engine indexing
    const metaTags = [
      { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'googlebot', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'bingbot', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'slurp', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'duckduckbot', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'baiduspider', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'yandexbot', content: 'noindex, nofollow, noarchive, nosnippet, noimageindex, nocache, nopreview' },
      { name: 'facebookexternalhit', content: 'noindex, nofollow' },
      { name: 'twitterbot', content: 'noindex, nofollow' },
      { name: 'linkedinbot', content: 'noindex, nofollow' },
      { name: 'whatsapp', content: 'noindex, nofollow' },
      { name: 'pinterest', content: 'noindex, nofollow' },
      { httpEquiv: 'cache-control', content: 'no-cache, no-store, must-revalidate' },
      { httpEquiv: 'pragma', content: 'no-cache' },
      { httpEquiv: 'expires', content: '0' },
      { name: 'referrer', content: 'no-referrer' }
    ];

    const createdTags = [];

    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) {
        meta.name = tag.name;
      } else if (tag.httpEquiv) {
        meta.httpEquiv = tag.httpEquiv;
      }
      meta.content = tag.content;
      document.head.appendChild(meta);
      createdTags.push(meta);
    });

    // Also set page title to indicate private content
    const originalTitle = document.title;
    document.title = 'Private Internal Document - Confidential - Not for Public Access';

    // Add viewport meta for mobile
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=no';
    document.head.appendChild(viewportMeta);
    createdTags.push(viewportMeta);

    // Cleanup function to remove meta tags when component unmounts
    return () => {
      createdTags.forEach(tag => {
        if (document.head.contains(tag)) {
          document.head.removeChild(tag);
        }
      });
      document.title = originalTitle;
    };
  }, []);

  const nervousSystemData = {
    brain: {
      name: "Brain",
      subtitle: "Founder / CEO",
      description: "Vision, Strategy, Abstract Thinking",
      icon: Brain,
      color: "from-slate-700 to-blue-800",
      details: "The central command responsible for vision, strategy, and abstract thinking. Sets the direction for the entire organism."
    },
    spinalCord: {
      name: "Spinal Cord",
      subtitle: "Chief of Staff",
      description: "Ops, Coordination, Reflex Execution",
      icon: Zap,
      color: "from-indigo-700 to-purple-800",
      details: "Operational backbone ensuring coordination and reflex execution across all systems."
    },
    neuralCore: [
      { name: "Product Insight", type: "Sight", icon: Eye, color: "from-blue-700 to-indigo-800", description: "Visual perception of product opportunities" },
      { name: "Market & Sales", type: "Hearing", icon: Ear, color: "from-teal-700 to-cyan-800", description: "Listening to market signals and feedback" },
      { name: "Risk Detection", type: "Smell", icon: AlertTriangle, color: "from-amber-700 to-orange-800", description: "Early warning system for threats" },
      { name: "Brand & Narrative", type: "Taste", icon: Smile, color: "from-purple-700 to-pink-800", description: "Company voice and brand experience" },
      { name: "Customer Touch", type: "Touch", icon: Hand, color: "from-violet-700 to-purple-800", description: "Direct customer experience interface" }
    ]
  };

  const organSystems = [
    { 
      name: "Cardiovascular", 
      subtitle: "Sales + Revenue", 
      description: "Revenue circulation and pipeline flow", 
      icon: Heart, 
      color: "from-red-600 to-red-800",
      customStyle: { background: 'linear-gradient(135deg, #C41229 0%, #8B0000 100%)' }
    },
    { 
      name: "Respiratory", 
      subtitle: "Brand + Marketing", 
      description: "Narrative and communication systems", 
      icon: Wind, 
      color: "from-blue-600 to-blue-800",
      customStyle: { background: 'linear-gradient(135deg, #2766D0 0%, #1E4A8C 100%)' }
    },
    { 
      name: "Digestive", 
      subtitle: "Customer Success", 
      description: "Feedback conversion and value extraction", 
      icon: Target, 
      color: "from-orange-600 to-orange-800",
      customStyle: { background: 'linear-gradient(135deg, #E86221 0%, #B8441A 100%)' }
    },
    { 
      name: "Excretory", 
      subtitle: "Finance + Legal", 
      description: "Compliance and resource regulation", 
      icon: DollarSign, 
      color: "from-green-600 to-green-800",
      customStyle: { background: 'linear-gradient(135deg, #1A7031 0%, #0F4419 100%)' }
    },
    { 
      name: "Endocrine", 
      subtitle: "Culture + HR", 
      description: "People operations and growth hormones", 
      icon: Users, 
      color: "from-purple-600 to-purple-800",
      customStyle: { background: 'linear-gradient(135deg, #6A2BFF 0%, #4A1DB8 100%)' }
    },
    { 
      name: "Skeletal", 
      subtitle: "Tech Infrastructure", 
      description: "Platforms and technical backbone", 
      icon: Cpu, 
      color: "from-slate-600 to-slate-800",
      customStyle: { background: 'linear-gradient(135deg, #475569 0%, #1E293B 100%)' }
    },
    { 
      name: "Integumentary", 
      subtitle: "UI/UX + Hardware", 
      description: "Interfaces and design systems", 
      icon: Layers, 
      color: "from-gray-600 to-gray-800",
      customStyle: { background: 'linear-gradient(135deg, #100F0F 0%, #374151 100%)' }
    }
  ];

  const verticals = {
    core: [
      { name: "Neuro-Motor (MiMo)", description: "Movement-based cognitive rehab", icon: Activity, color: "from-blue-700 to-indigo-800" },
      { name: "Brain Music", description: "EEG + Sound brain modulation", icon: Heart, color: "from-purple-700 to-pink-800" },
      { name: "Speech Therapy", description: "AI + Human hybrid solutions", icon: Smile, color: "from-teal-700 to-cyan-800" },
      { name: "Biomechanics", description: "Gait, posture analytics", icon: Target, color: "from-amber-700 to-orange-800" },
      { name: "Rehabilitation", description: "Stroke recovery, elder care", icon: Stethoscope, color: "from-rose-700 to-pink-800" },
      { name: "Mental Health", description: "Cognitive aging, mood therapy", icon: Brain, color: "from-cyan-700 to-blue-800" },
      { name: "Education", description: "Early neurodevelopment kits", icon: BookOpen, color: "from-yellow-700 to-amber-800" }
    ],
    platform: [
      { name: "ZeuronOS", description: "OS for Brain Health Consoles", icon: Cpu, color: "from-slate-700 to-gray-800" },
      { name: "Cognition + GenAI", description: "ZeeGPT, Brain AI agents", icon: Brain, color: "from-indigo-700 to-purple-800" },
      { name: "Game Studio", description: "Exergames, neuro-simulations", icon: Gamepad2, color: "from-blue-700 to-cyan-800" }
    ],
    frontier: [
      { name: "Sports Performance", description: "Brain-body training for athletes", icon: Zap, color: "from-emerald-700 to-green-800" },
      { name: "Defense & Tactical", description: "Simulation-based resilience", icon: Shield, color: "from-red-700 to-rose-800" },
      { name: "Space Neurotech", description: "Cognitive performance in low-gravity", icon: Rocket, color: "from-violet-700 to-purple-800" },
      { name: "Neuro-Robotics", description: "Brain-controlled therapeutic systems", icon: Cog, color: "from-teal-700 to-cyan-800" }
    ]
  };

  const substrate = [
    { name: "Computational Hardware", description: "High-performance, energy-efficient foundation", icon: Cpu, color: "from-slate-700 to-gray-800" },
    { name: "Zeuron OS", description: "Brain-inspired OS for parallelism", icon: Layers, color: "from-blue-700 to-indigo-800" },
    { name: "Sensor & Actuation", description: "Real-world data collection interface", icon: Target, color: "from-emerald-700 to-teal-800" },
    { name: "Brain-Inspired AI", description: "AI models for neuro-health", icon: Brain, color: "from-purple-700 to-pink-800" },
    { name: "App Engine", description: "Immersive human-machine interaction", icon: Gamepad2, color: "from-cyan-700 to-blue-800" },
    { name: "AI Analytics", description: "Real-time cognitive insights", icon: Activity, color: "from-orange-700 to-red-800" }
  ];

  const reflexArc = [
    { step: "Market Stimulus", icon: AlertTriangle, color: "from-amber-700 to-orange-800" },
    { step: "Sensory Core", icon: Eye, color: "from-blue-700 to-cyan-800" },
    { step: "Brain", icon: Brain, color: "from-purple-700 to-indigo-800" },
    { step: "Spinal Cord", icon: Zap, color: "from-indigo-700 to-purple-800" },
    { step: "Org Systems", icon: Heart, color: "from-emerald-700 to-teal-800" },
    { step: "Verticals", icon: Layers, color: "from-pink-700 to-purple-800" },
    { step: "Action", icon: Target, color: "from-blue-700 to-indigo-800" },
    { step: "Feedback", icon: Hand, color: "from-yellow-700 to-amber-800" },
    { step: "Learn", icon: BookOpen, color: "from-teal-700 to-cyan-800" },
    { step: "Adapt", icon: Cog, color: "from-rose-700 to-pink-800" }
  ];

  const SystemCard = ({ system, onClick, size = 'normal' }) => {
    const Icon = system.icon;
    const isLarge = size === 'large';
    
    return (
      <div
        onClick={() => onClick && onClick(system)}
        className={`${
          system.customStyle ? '' : `bg-gradient-to-br ${system.color}`
        } ${
          isLarge 
            ? 'p-4 sm:p-5 lg:p-6' 
            : 'p-3 sm:p-4 lg:p-5'
        } rounded-lg lg:rounded-xl 
        transform transition-all duration-500 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/20 
        text-white border border-white/10 backdrop-blur-sm hover:border-white/30 group relative overflow-hidden
        ${onClick ? 'cursor-pointer' : ''} touch-manipulation`}
        style={system.customStyle || {}}
      >
        <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="relative z-10">
          <div className={`flex items-center ${isLarge ? 'space-x-2 lg:space-x-3' : 'space-x-2'} ${isLarge ? 'mb-3 lg:mb-4' : 'mb-2 lg:mb-3'}`}>
            <div className={`${isLarge ? 'p-2 lg:p-3' : 'p-1.5 lg:p-2'} bg-white/15 rounded-lg backdrop-blur-sm flex-shrink-0`}>
              <Icon size={isLarge ? 18 : 16} className={isLarge ? 'lg:w-5 lg:h-5' : 'lg:w-4 lg:h-4'} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={`font-bold ${
                isLarge 
                  ? 'text-sm sm:text-base lg:text-lg' 
                  : 'text-xs sm:text-sm lg:text-base'
              } truncate leading-tight`}>
                {system.name}
              </h3>
              {system.subtitle && (
                <p className={`${
                  isLarge 
                    ? 'text-xs lg:text-sm' 
                    : 'text-xs'
                } opacity-90 truncate`}>
                  {system.subtitle}
                </p>
              )}
            </div>
          </div>
          <p className={`${
            isLarge 
              ? 'text-xs lg:text-sm' 
              : 'text-xs'
          } opacity-80 leading-relaxed line-clamp-2`}>
            {system.description}
          </p>
        </div>
      </div>
    );
  };

  const CollapsibleSection = ({ id, title, subtitle, icon: Icon, color, customStyle, defaultExpanded = true, children }) => {
    const isExpanded = collapsedSections[id] !== undefined ? !collapsedSections[id] : defaultExpanded;
    
    const toggleSection = () => {
      setCollapsedSections(prev => ({
        ...prev,
        [id]: isExpanded
      }));
    };

    const closeSection = (e) => {
      e.stopPropagation();
      setCollapsedSections(prev => ({
        ...prev,
        [id]: true
      }));
    };

    return (
      <div className="mb-8 lg:mb-12">
        <div className="flex items-center justify-between p-4 lg:p-6 bg-gray-800/30 rounded-xl lg:rounded-2xl border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 mb-4 lg:mb-6">
          <button
            onClick={toggleSection}
            className="flex items-center space-x-3 lg:space-x-4 flex-1 text-left group"
          >
            <div 
              className={`p-3 lg:p-4 ${customStyle ? '' : `bg-gradient-to-r ${color}`} rounded-xl lg:rounded-2xl group-hover:scale-105 transition-transform duration-300`}
              style={customStyle || {}}
            >
              <Icon size={24} className="lg:w-8 lg:h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1">{title}</h2>
              {subtitle && <p className="text-lg sm:text-xl lg:text-2xl text-gray-300">{subtitle}</p>}
            </div>
          </button>
          
          <div className="flex items-center space-x-2">
            {isExpanded && (
              <button
                onClick={closeSection}
                className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-gray-400 hover:text-white touch-manipulation"
                title="Close section"
              >
                <X size={20} />
              </button>
            )}
            <button
              onClick={toggleSection}
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors text-white touch-manipulation"
              title={isExpanded ? "Collapse section" : "Expand section"}
            >
              {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
            </button>
          </div>
        </div>
        
        <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-2 lg:px-4">
            {children}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* 
        CONFIDENTIAL INTERNAL DOCUMENT - ZEURON.AI ORGANIZATIONAL STRUCTURE
        - NOT FOR PUBLIC ACCESS OR DISTRIBUTION
        - NO SEARCH ENGINE INDEXING PERMITTED
        - PROPRIETARY AND CONFIDENTIAL INFORMATION
        - AUTHORIZED PERSONNEL ONLY
      */}
      <div className="min-h-screen bg-gradient-to-br from-gray-950 via-blue-950 to-indigo-950 text-white">
        {/* Neural Background */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-20 left-20 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-32 w-1 h-1 bg-purple-400 rounded-full animate-pulse delay-300"></div>
          <div className="absolute bottom-32 left-40 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse delay-700"></div>
          <div className="absolute bottom-20 right-20 w-2 h-2 bg-pink-400 rounded-full animate-pulse delay-1000"></div>
        </div>

        {/* Header */}
        <div className="relative z-10 text-center py-8 lg:py-12 px-4">
          <img
          src="/logo.png"
          alt="Zeuron Logo"
          className="mx-auto object-contain w-auto h-auto
                    max-h-[180px] sm:max-h-[120px] md:max-h-[90px]
                    mb-8 transition-transform hover:scale-105"
          />
          <p className="text-xl sm:text-2xl lg:text-3xl text-blue-200 mb-2 lg:mb-3">The Living Company</p>
          <p className="text-base sm:text-lg lg:text-xl text-gray-400 px-4 mb-6 lg:mb-8">We don't build org charts. We build organisms.</p>
          
          {/* Global Controls */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={expandAll}
                disabled={expandedCount === allSections.length}
                className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-500 hover:to-emerald-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg lg:rounded-xl font-medium transition-all duration-300 text-white shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100 touch-manipulation"
              >
                <ChevronDown size={16} className="lg:w-5 lg:h-5" />
                <span className="text-sm sm:text-base">Expand All</span>
              </button>
              
              <button
                onClick={collapseAll}
                disabled={expandedCount === 0}
                className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed rounded-lg lg:rounded-xl font-medium transition-all duration-300 text-white shadow-lg hover:shadow-xl hover:scale-105 disabled:hover:scale-100 touch-manipulation"
              >
                <ChevronUp size={16} className="lg:w-5 lg:h-5" />
                <span className="text-sm sm:text-base">Collapse All</span>
              </button>
            </div>
            
            {/* Section Counter */}
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-gray-800/50 rounded-lg lg:rounded-xl border border-gray-700/50">
              <Layers size={16} className="text-blue-400" />
              <span className="text-sm sm:text-base text-gray-300">
                {expandedCount} of {allSections.length} sections expanded
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16">
          <div className="max-w-7xl mx-auto">
            
            {/* Founder's Office Section */}
            <CollapsibleSection
              id="nervous-system"
              title="Founder's Office"
              subtitle="The Nervous System"
              icon={Brain}
              color="from-blue-600 to-purple-700"
            >
              <div className="space-y-6 lg:space-y-8">
                <div className="grid gap-4 lg:gap-6 mb-6 lg:mb-8">
                  <SystemCard system={nervousSystemData.brain} onClick={setSelectedItem} size="large" />
                  <SystemCard system={nervousSystemData.spinalCord} onClick={setSelectedItem} size="large" />
                </div>

                <div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-6 text-center text-cyan-300">Neural Core (5 Senses)</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
                    {nervousSystemData.neuralCore.map((sense, idx) => (
                      <SystemCard key={idx} system={sense} onClick={setSelectedItem} />
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Organ Systems Section */}
            <CollapsibleSection
              id="organ-systems"
              title="Organ Systems"
              subtitle="Shared Infrastructure"
              icon={Heart}
              color="from-purple-600 to-pink-700"
              customStyle={{ background: 'linear-gradient(135deg, #C41229 0%, #8B0000 100%)' }}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
                {organSystems.map((system, idx) => (
                  <SystemCard key={idx} system={system} onClick={setSelectedItem} />
                ))}
              </div>
            </CollapsibleSection>

            {/* Body Functions Section */}
            <CollapsibleSection
              id="body-functions"
              title="Body Functions"
              subtitle="Zeuron Verticals"
              icon={Layers}
              color="from-green-600 to-teal-700"
            >
              <div className="space-y-6 lg:space-y-8">
                <div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-5 text-blue-300">Core Verticals</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
                    {verticals.core.map((vertical, idx) => (
                      <SystemCard key={idx} system={vertical} onClick={setSelectedItem} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-5 text-purple-300">Platform Verticals</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                    {verticals.platform.map((vertical, idx) => (
                      <SystemCard key={idx} system={vertical} onClick={setSelectedItem} />
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-4 lg:mb-5 text-cyan-300">Strategic Frontier Verticals</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 lg:gap-4">
                    {verticals.frontier.map((vertical, idx) => (
                      <SystemCard key={idx} system={vertical} onClick={setSelectedItem} />
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Neural Substrate Section */}
            <CollapsibleSection
              id="neural-substrate"
              title="Neural Substrate"
              subtitle="Foundational Tech Layer"
              icon={Cpu}
              color="from-cyan-600 to-blue-700"
            >
              <div className="space-y-6 lg:space-y-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3 lg:gap-4">
                  {substrate.map((component, idx) => (
                    <SystemCard key={idx} system={component} onClick={setSelectedItem} />
                  ))}
                </div>
                
                <div className="text-center p-4 lg:p-6 bg-gradient-to-r from-purple-900/20 to-blue-900/20 rounded-xl border border-purple-500/20">
                  <p className="text-sm lg:text-base text-purple-200">Patented during MiMo development — now enables all current and future systems</p>
                </div>
              </div>
            </CollapsibleSection>

            {/* Reflex Arc Section */}
            <CollapsibleSection
              id="reflex-arc"
              title="Reflex Arc"
              subtitle="Adaptive Learning Company"
              icon={Zap}
              color="from-yellow-600 to-orange-700"
            >
              <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
                  {reflexArc.map((step, idx) => {
                    const Icon = step.icon;
                    return (
                      <div key={idx} className="relative">
                        <div className={`bg-gradient-to-br ${step.color} p-3 lg:p-4 rounded-lg lg:rounded-xl text-white transform transition-all duration-300 hover:scale-105 border border-white/10`}>
                          <div className="flex flex-col items-center space-y-2 text-center">
                            <Icon size={16} className="lg:w-4 lg:h-4" />
                            <span className="font-medium text-xs lg:text-sm leading-tight">{step.step}</span>
                          </div>
                        </div>
                        {idx < reflexArc.length - 1 && (
                          <ChevronRight size={14} className="absolute -right-2 lg:-right-3 top-1/2 transform -translate-y-1/2 text-blue-400 hidden sm:block opacity-60" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CollapsibleSection>

          </div>
        </div>

        {/* Modal for detailed view */}
        {selectedItem && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 border border-blue-500/30 rounded-xl lg:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className={`bg-gradient-to-r ${selectedItem.color} p-4 lg:p-6 rounded-t-xl lg:rounded-t-2xl`}>
                <div className="flex justify-between items-start text-white">
                  <div className="flex items-center space-x-3 lg:space-x-4 min-w-0 flex-1">
                    <div className="p-2 lg:p-3 bg-white/20 rounded-lg lg:rounded-xl backdrop-blur-sm flex-shrink-0">
                      <selectedItem.icon size={24} className="lg:w-8 lg:h-8" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg sm:text-xl lg:text-2xl font-bold leading-tight">{selectedItem.name}</h2>
                      {selectedItem.subtitle && <p className="text-sm lg:text-base opacity-90 leading-tight">{selectedItem.subtitle}</p>}
                    </div>
                  </div>
                  <button 
                    onClick={() => setSelectedItem(null)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors touch-manipulation ml-2 flex-shrink-0"
                  >
                    <X size={18} className="lg:w-5 lg:h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-4 lg:p-6 text-white">
                <p className="text-sm lg:text-base text-gray-300 leading-relaxed">{selectedItem.details || selectedItem.description}</p>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .touch-manipulation {
            touch-action: manipulation;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
          }
          
          /* Mobile-first responsive optimizations */
          @media (max-width: 640px) {
            .touch-manipulation {
              min-height: 44px;
              min-width: 44px;
            }
            
            .grid {
              gap: 0.75rem;
            }
          }
          
          /* Smooth scrolling */
          html {
            scroll-behavior: smooth;
          }
          
          /* Custom scrollbar */
          ::-webkit-scrollbar {
            width: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: rgba(31, 41, 55, 0.5);
          }
          
          ::-webkit-scrollbar-thumb {
            background: rgba(59, 130, 246, 0.5);
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(59, 130, 246, 0.7);
          }
        `}</style>
      </div>
    </>
  );
};

export default ZeuronOrgChart;